import { Icons } from '@/components/icons';
import { Badge } from '@/components/primitives/Badge';
import { Button } from '@/components/primitives/Button';
import { Typography } from '@/components/primitives/Typography';
import ServiceTextButton from '@/components/purchase/journey/ServiceTextButton';
import useLocalizedCurrencyFormatter from '@/hooks/useLocalizedCurrencyFormatter';
import { TransText } from '@/i18n/trans/text';
import { useDispatch } from '@/store/utils';
import { type SearchJourneyOffersDTO } from '@/types/dto/agent';
import { cn } from '@/utils/cn';
import { format, formatDuration } from '@/utils/dateTime';
import { type GTM_EVENT, trackCustomEvent } from '@/utils/gtm';
import type { ActionCreatorWithPayload } from '@reduxjs/toolkit';
import { type FC } from 'react';

interface JourneyCardProps {
  journey: SearchJourneyOffersDTO;
  warningMessage: string | null;
  onContinue: () => void;
  setSelectedJourney: ActionCreatorWithPayload<SearchJourneyOffersDTO>;
  journeySelectedEvent: GTM_EVENT;
}
function renderPrice(
  amount: number | undefined,
  numberFormatter: Intl.NumberFormat
) {
  if (amount === undefined) {
    return null;
  }

  const parts = numberFormatter.formatToParts(amount);

  const integerParts = parts
    .filter((part) => part.type === 'integer' || part.type === 'group')
    .map((part) => part.value)
    .join('');
  const decimalPart =
    parts.find((part) => part.type === 'fraction')?.value || '00';
  const decimalSeparator =
    parts.find((part) => part.type === 'decimal')?.value || '.';
  const [currency, isCurrencyBeforePrice] = (() => {
    const currencyIndex = parts.findIndex((part) => part.type === 'currency');

    return [parts[currencyIndex]?.value || '', currencyIndex === 0];
  })();

  return (
    <>
      {isCurrencyBeforePrice && (
        <Typography variant="subtitle" className="mr-1">
          {currency}
        </Typography>
      )}
      <Typography variant="heading2">{integerParts}</Typography>
      {decimalPart ? (
        <>
          <Typography variant="heading2">{decimalSeparator}</Typography>
          <Typography variant="subtitle">
            {decimalPart.padEnd(2, '0')}
          </Typography>
        </>
      ) : null}
      {!isCurrencyBeforePrice && (
        <Typography variant="subtitle" className="ml-1">
          {currency}
        </Typography>
      )}
    </>
  );
}

const JourneyCard: FC<JourneyCardProps> = ({
  journey,
  warningMessage,
  onContinue,
  setSelectedJourney,
  journeySelectedEvent,
}) => {
  const dispatch = useDispatch();
  const numberFormatter = useLocalizedCurrencyFormatter();
  const isDisabled = !!warningMessage || !journey.lowestPrice;

  const departureTime =
    journey.departureTime && format(journey.departureTime, 'HH:mm');
  const arrivalTime =
    journey.arrivalTime && format(journey.arrivalTime, 'HH:mm');
  const hasDiscount = Boolean(
    journey.trips?.find((trip) =>
      trip.offers?.find((offer) => offer.appliedReductions?.length)
    )
  );
  const serviceTexts = (journey.trips ?? []).flatMap((trip) =>
    (trip.legs ?? []).flatMap((leg) =>
      (leg.serviceAttributes ?? []).filter(
        ({ code }) => code === 'SERVICE_TEXT'
      )
    )
  );

  return (
    <>
      <div
        className={cn(
          'flex flex-col gap-2 rounded-l-xl border border-r-0 border-neutral bg-white px-4 py-3',
          {
            'bg-greyscale-100 text-neutral': isDisabled,
            'laptop:flex-row-reverse laptop:justify-between':
              serviceTexts.length,
          }
        )}
      >
        <ServiceTextButton serviceTexts={serviceTexts} />

        <div className="flex flex-col gap-2">
          <div className="flex flex-row items-center gap-1 py-1 text-dark">
            <Typography variant="times" asChild>
              <h2>{departureTime}</h2>
            </Typography>
            <Typography variant="times">
              <span>-</span>
            </Typography>
            <Typography variant="times" asChild>
              <h2>{arrivalTime}</h2>
            </Typography>
          </div>

          <div className="flex w-[200px]">
            <div className="flex w-1/2 flex-row items-center gap-1">
              <Icons.time
                height={14}
                width={14}
                className={cn('text-primary', {
                  'text-neutral': isDisabled,
                })}
              />
              <Typography variant="body2" className="text-neutral">
                {formatDuration(journey.duration)}
              </Typography>
            </div>
            <div className="w-full">
              {journey.transfers ? (
                <Typography className="text-error-text" variant="body2">
                  <TransText
                    i18nKey="journeyTransfersCount"
                    values={{ count: journey.transfers }}
                  />
                </Typography>
              ) : (
                <Typography variant="body2" className="text-primary">
                  <TransText i18nKey="direct" />
                </Typography>
              )}
            </div>
          </div>
        </div>
      </div>
      <div
        className={cn('rounded-r-xl border border-neutral bg-primary', {
          'bg-neutral': isDisabled, // bg-primary and bg-neutral used here to fill weird gap between button and parent container
        })}
      >
        <Button
          className="relative flex h-full w-full flex-col rounded-l-none rounded-r-xl border-none"
          disabled={isDisabled}
          data-testid="journey-card-action-button"
          onClick={() => {
            dispatch(setSelectedJourney(journey));
            trackCustomEvent(journeySelectedEvent);
            onContinue();
          }}
        >
          {isDisabled ? (
            <Typography variant="body1" className="text-wrap">
              {warningMessage ?? <TransText i18nKey="notAvailable" />}
            </Typography>
          ) : (
            <div className="flex flex-col items-center gap-1">
              <Typography variant="body2">
                <TransText i18nKey="from" />
              </Typography>
              <div className="flex items-baseline">
                {renderPrice(journey.lowestPrice?.amount, numberFormatter)}
                <span>
                  <Icons.chevronRight className="-mb-1" />
                </span>
              </div>
            </div>
          )}
          {hasDiscount && (
            <Badge
              className="absolute -right-2 -top-0.5 rotate-[10deg]"
              variant="success"
            >
              <Icons.dollarCircle width={12} height={12} />
              <TransText i18nKey="discount" />
            </Badge>
          )}
        </Button>
      </div>
    </>
  );
};

export default JourneyCard;
